import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Editor from '@monaco-editor/react';
import NotificationModal from '../shared/NotificationModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface BaseWorkflowEditorProps {
  filename: string | null; // null means creating new
  onClose: (saved: boolean) => void;
}

const BaseWorkflowEditor: React.FC<BaseWorkflowEditorProps> = ({ filename, onClose }) => {
  const [workflowName, setWorkflowName] = useState('');
  const [originalFilename, setOriginalFilename] = useState<string | null>(null);
  const [jsonContent, setJsonContent] = useState('{\n  \n}');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Notification modal state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info'>('info');

  useEffect(() => {
    if (filename) {
      // Editing existing workflow
      setWorkflowName(filename.replace('.json', ''));
      setOriginalFilename(filename);
      fetchWorkflowContent();
    } else {
      // Creating new workflow
      setWorkflowName('');
      setOriginalFilename(null);
      setJsonContent('{\n  \n}');
    }
  }, [filename]);

  const fetchWorkflowContent = async () => {
    if (!filename) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/base-workflows/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Use raw_content for Monaco Editor (preserves formatting)
      setJsonContent(response.data.raw_content || JSON.stringify(response.data.content, null, 2));
    } catch (error: any) {
      console.error('Failed to fetch workflow:', error);
      setError(error.response?.data?.error || 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const validateJSON = (): boolean => {
    try {
      JSON.parse(jsonContent);
      setError(null);
      return true;
    } catch (e: any) {
      setError(`Invalid JSON: ${e.message}`);
      return false;
    }
  };

  const handleSave = async () => {
    if (!workflowName.trim()) {
      setError('Workflow name is required');
      return;
    }

    if (!validateJSON()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');
      const workflowFilename = workflowName.endsWith('.json') ? workflowName : `${workflowName}.json`;

      if (filename) {
        // Update existing workflow
        const isRenamed = originalFilename !== workflowFilename;
        const requestBody: any = { content: jsonContent };

        // If the name has changed, include newName in the request
        if (isRenamed) {
          requestBody.newName = workflowFilename;
        }

        const response = await axios.put(
          `${API_BASE}/base-workflows/${filename}`,
          requestBody,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Success - show appropriate message
        const message = isRenamed
          ? 'Base workflow renamed and updated successfully! Changes committed to GitHub and will deploy to Kubernetes.'
          : 'Base workflow updated successfully! Changes committed to GitHub and will deploy to Kubernetes.';
        setNotificationMessage(message);
        setNotificationType('success');
        setShowNotification(true);
      } else {
        // Create new workflow
        await axios.post(
          `${API_BASE}/base-workflows`,
          { name: workflowFilename, content: jsonContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNotificationMessage('Base workflow created successfully! Committed to GitHub and will deploy to Kubernetes.');
        setNotificationType('success');
        setShowNotification(true);
      }
    } catch (error: any) {
      console.error('Failed to save workflow:', error);
      setError(error.response?.data?.error || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      setJsonContent(value);
      setError(null);
    }
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonContent);
      setJsonContent(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (e: any) {
      setError(`Cannot format: ${e.message}`);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={() => onClose(false)}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {filename ? 'Edit Base Workflow' : 'Create Base Workflow'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Changes will be committed to GitHub and deployed to Kubernetes
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 p-6 overflow-auto">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-gray-600">Loading workflow...</div>
            </div>
          ) : (
            <>
              {/* Workflow Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow Name *
                </label>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="e.g., MyWorkflow (will be saved as MyWorkflow.json)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {filename && (
                  <p className="text-xs text-gray-500 mt-1">
                    You can edit the workflow name to rename it
                  </p>
                )}
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  {error}
                </div>
              )}

              {/* JSON Editor */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Workflow JSON *
                  </label>
                  <button
                    onClick={handleFormat}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Format JSON
                  </button>
                </div>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <Editor
                    height="500px"
                    defaultLanguage="json"
                    value={jsonContent}
                    onChange={handleEditorChange}
                    theme="vs-light"
                    options={{
                      minimap: { enabled: true },
                      fontSize: 14,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      tabSize: 2,
                      wordWrap: 'on',
                      formatOnPaste: true,
                      formatOnType: true,
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Tip: Press Ctrl+Shift+F (Windows/Linux) or Cmd+Shift+F (Mac) to auto-format
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={() => onClose(false)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : filename ? 'Update & Commit' : 'Create & Commit'}
          </button>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        onClose={() => {
          setShowNotification(false);
          if (notificationType === 'success') {
            onClose(true);
          }
        }}
        message={notificationMessage}
        type={notificationType}
      />
    </div>
  );
};

export default BaseWorkflowEditor;
